import { showAddOrEditFineState } from "@/atoms";
import CustomInput from "@/components/common/inputs/CustomInput";
import useAuth from "@/hooks/useAuth";
import { useGetAllCouncils } from "@/services/councils";
import { useGetServicesByCouncil } from "@/services/services";
import { useCreateFine, useUpdateFine } from "@/services/fines";
import { Button, NumberInput, Select } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useQueryClient } from "@tanstack/react-query";
import { ModalLayout } from "moh-ui";
import { useEffect, useState } from "react";
import { useRecoilState } from "recoil";

export default function AddUpdateFine() {
    const [show, setShow] = useRecoilState(showAddOrEditFineState);
    const { user } = useAuth();
    const [data, setData] = useState({
        baseRatio: 0,
        safePeriodDays: 0,
        increaseUntilDay: 0,
        councilId: null as string | null,
        serviceId: null as string | null,
    });

    const {
        mutate: createFine,
        isPending: creatingFine
    } = useCreateFine();

    const {
        mutate: updateFine,
        isPending: updatingFine
    } = useUpdateFine();

    const queryClient = useQueryClient();

    const {
        data: services,
    } = useGetServicesByCouncil({
        councilId: data.councilId || undefined,
        enabled: !!data.councilId,
    });

    const {
        data: councils,
    } = useGetAllCouncils();

    useEffect(() => {
        if (show?.action === 'edit' && show.fine) {
            setData({
                baseRatio: show.fine.baseRatio,
                safePeriodDays: show.fine.safePeriodDays,
                increaseUntilDay: show.fine.increaseUntilDay,
                councilId: show.fine.council.id,
                serviceId: show.fine.service.id,
            });
        } else {
            setData({
                baseRatio: 0,
                safePeriodDays: 0,
                increaseUntilDay: 0,
                councilId: user?.roles[0].roleName === "ADMINCOUNCIL" ? user?.usercouncil[0]?.id : null,
                serviceId: null,
            });
        }
    }, [show, user]);

    const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const params = {
            ...data,
            baseRatio: data.baseRatio,
            councilId: data.councilId!,
            serviceId: data.serviceId!,
        };

        if (show?.action === 'add') {
            createFine(params, {
                onSuccess: () => {
                    notifications.show({
                        title: "Success",
                        message: "Fine Added Successfully",
                        color: "green",
                    });
                    if (user?.roles[0].roleName === "ADMINCOUNCIL") {
                        queryClient.invalidateQueries({
                            queryKey: ["fines", user?.usercouncil[0]?.id],
                        })
                    } else {
                        queryClient.invalidateQueries({
                            queryKey: ["fines"],
                        })
                    }
                    setShow(null);
                },
                onError: (error) => {
                    notifications.show({
                        title: "Error",
                        message: error.message || "Failed to add fine",
                        color: "red",
                    });
                }
            });
        } else if (show?.action === 'edit' && show.fine) {
            updateFine({
                id: show.fine.id,
                ...params,
            }, {
                onSuccess: () => {
                    notifications.show({
                        title: "Success",
                        message: "Fine Updated Successfully",
                        color: "green",
                    });
                    if (user?.roles[0].roleName === "ADMINCOUNCIL") {
                        queryClient.invalidateQueries({
                            queryKey: ["fines", user?.usercouncil[0]?.id],
                        })
                    } else {
                        queryClient.invalidateQueries({
                            queryKey: ["fines"],
                        })
                    }
                    setShow(null);
                },
                onError: (error) => {
                    notifications.show({
                        title: "Error",
                        message: error.message || "Failed to update fine",
                        color: "red",
                    });
                }
            });
        }
    };

    return (
        <ModalLayout
            open={Boolean(show?.show)}
            onClose={() => setShow(null)}
        >
            <div className="mb-5">
                <h2 className="text-lg font-semibold">
                    {show?.action === 'add' ? 'Add Fine' : 'Update Fine'}
                </h2>
                <p className="text-sm text-gray-500">
                    {show?.action === 'add' ? 'Fill in the details to create a new fine.' : 'Update the fine details as needed.'}
                </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                {user?.roles[0].roleName === "ADMIN" && (
                    <CustomInput label="Select Council" alwaysFocused>
                        <Select
                            value={data.councilId ?? ""}
                            onChange={(val) => setData({ ...data, councilId: val })}
                            data={councils?.data.map((item) => ({
                                value: item.id,
                                label: item.councilName + " (" + item.councilCode + ")",
                            })) || []}
                            placeholder="Select Council"
                            required
                            className="w-full"
                        />
                    </CustomInput>
                )}

                <CustomInput label="Select Service" alwaysFocused>
                    <Select
                        value={data.serviceId ?? ""}
                        onChange={(val) => setData({ ...data, serviceId: val })}
                        data={services?.data?.map((item) => ({
                            value: item.id,
                            label: item.name,
                        })) || []}
                        placeholder="Select Service"
                        disabled={!data.councilId}
                        required
                        className="w-full"
                    />
                </CustomInput>

                <CustomInput label="Base Ratio (%)" alwaysFocused>
                    <NumberInput
                        value={data.baseRatio * 100}
                        onChange={(val) => setData({ ...data, baseRatio: (Number(val) || 0) / 100 })}
                        min={0}
                        max={100}
                        required
                        className="w-full"
                    />
                </CustomInput>

                <CustomInput label="Safe Period (Days)" alwaysFocused>
                    <NumberInput
                        value={data.safePeriodDays}
                        onChange={(val) => setData({ ...data, safePeriodDays: Number(val) || 0 })}
                        min={0}
                        required
                        className="w-full"
                    />
                </CustomInput>

                <CustomInput label="Increase Until (Days)" alwaysFocused>
                    <NumberInput
                        value={data.increaseUntilDay}
                        onChange={(val) => setData({ ...data, increaseUntilDay: Number(val) || 0 })}
                        min={0}
                        required
                        className="w-full"
                    />
                </CustomInput>

                <div className="flex justify-end items-center gap-4 pt-4 border-t">
                    <Button
                        variant="outline"
                        color="gray"
                        onClick={() => setShow(null)}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        loading={creatingFine || updatingFine}
                    >
                        {show?.action === 'add' ? 'Create Fine' : 'Update Fine'}
                    </Button>
                </div>
            </form>
        </ModalLayout>
    );
}
